import type { OfferDraftRow, OfferDraftRowKind, PositionRowId } from '../../../types';
import type { InvoiceLedgerSnapshotV1, PartialInvoiceMode } from './invoiceLedgerSnapshot';
import { isLedgerSnapshotV1 } from './invoiceLedgerSnapshot';
import { isPositionRowId, positionRowIdKey } from './positionRowId';

export type { PartialInvoiceMode } from './invoiceLedgerSnapshot';

export type PositionBillingInvoiceRow = {
  id: string;
  document_type: string;
  status?: string | null;
  ledger_snapshot?: unknown;
};

export type PositionBillingRowInput = {
  id: string;
  kind: OfferDraftRowKind;
  leadPositionId: PositionRowId | null;
  quantity: number;
};

export type PositionBillingRowState = {
  rowKey: string;
  rowId: string;
  kind: OfferDraftRowKind;
  offerQty: number;
  alreadyInvoicedQty: number;
  remainingQty: number;
  invoiceQty: number;
  overInvoiced: boolean;
};

export function isBillablePositionRowKind(kind: OfferDraftRowKind): boolean {
  return kind !== 'title' && kind !== 'text' && kind !== 'image';
}

export function billingRowKey(
  leadPositionId: PositionRowId | null | undefined,
  draftRowId: string,
): string | null {
  if (isPositionRowId(leadPositionId)) return positionRowIdKey(leadPositionId);
  const id = String(draftRowId ?? '').trim();
  if (id) return `draft:${id}`;
  return null;
}

export function billingRowKeyFromDraftRow(row: Pick<OfferDraftRow, 'id' | 'leadPositionId'>): string | null {
  return billingRowKey(row.leadPositionId, row.id);
}

function resolvePartialInvoiceMode(snap: InvoiceLedgerSnapshotV1): PartialInvoiceMode | null {
  if (
    snap.partialInvoiceMode === 'position' ||
    snap.partialInvoiceMode === 'percent' ||
    snap.partialInvoiceMode === 'amount'
  ) {
    return snap.partialInvoiceMode;
  }
  if (snap.isPartialInvoice) {
    const hasPositionQty =
      snap.positionInvoiceQuantities != null &&
      Object.keys(snap.positionInvoiceQuantities).length > 0;
    if (hasPositionQty) return 'position';
    const economicRows = (snap.draftRows ?? []).filter((r) => isBillablePositionRowKind(r.kind));
    if (economicRows.length === 0) return snap.partialInvoiceAmount != null ? 'amount' : 'percent';
    if (economicRows.length === 1 && economicRows[0]?.unit === 'psch') {
      return snap.partialInvoiceAmount != null ? 'amount' : 'percent';
    }
    return 'position';
  }
  return null;
}

function hasPositionBilledRows(snap: InvoiceLedgerSnapshotV1): boolean {
  if (snap.positionInvoiceQuantities && Object.keys(snap.positionInvoiceQuantities).length > 0) {
    return true;
  }
  const economicRows = (snap.draftRows ?? []).filter((r) => isBillablePositionRowKind(r.kind));
  if (economicRows.length === 0) return false;
  if (snap.isFinalInvoice && economicRows.length === 1 && snap.finalSnapshotLine) {
    return false;
  }
  return true;
}

function quantitiesFromSnapshotMap(
  snap: InvoiceLedgerSnapshotV1,
  sign: 1 | -1,
): Record<string, number> {
  const out: Record<string, number> = {};
  const map = snap.positionInvoiceQuantities;
  if (map && typeof map === 'object') {
    for (const [key, rawQty] of Object.entries(map)) {
      const qty = Number(rawQty);
      if (!Number.isFinite(qty) || qty === 0) continue;
      out[key] = (out[key] ?? 0) + sign * qty;
    }
    return out;
  }
  for (const row of snap.draftRows ?? []) {
    if (!isBillablePositionRowKind(row.kind)) continue;
    const key = billingRowKeyFromDraftRow(row);
    if (!key) continue;
    const qty = Number(row.quantity);
    if (!Number.isFinite(qty) || qty === 0) continue;
    out[key] = (out[key] ?? 0) + sign * qty;
  }
  return out;
}

export function extractBilledQuantitiesFromSnapshot(
  snapshot: unknown,
  documentType: string,
): Record<string, number> {
  if (!isLedgerSnapshotV1(snapshot)) return {};
  const snap = snapshot;
  const docType = String(documentType ?? '').trim();
  const sign: 1 | -1 = docType === 'credit_note' || docType === 'invoice_correction' ? -1 : 1;

  if (docType === 'partial_invoice') {
    const mode = resolvePartialInvoiceMode(snap);
    if (mode === 'percent' || mode === 'amount') return {};
    return quantitiesFromSnapshotMap(snap, sign);
  }

  if (docType === 'invoice') {
    return quantitiesFromSnapshotMap(snap, sign);
  }

  if (docType === 'final_invoice') {
    if (snap.partialInvoiceMode === 'percent' && !hasPositionBilledRows(snap)) {
      return {};
    }
    if (snap.isFinalInvoice && snap.finalSnapshotLine && !hasPositionBilledRows(snap)) {
      return {};
    }
    return quantitiesFromSnapshotMap(snap, sign);
  }

  if (docType === 'credit_note' || docType === 'invoice_correction') {
    return quantitiesFromSnapshotMap(snap, -1);
  }

  return {};
}

export function aggregateInvoicedQuantities(
  invoices: PositionBillingInvoiceRow[],
): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const inv of invoices) {
    const status = String(inv.status ?? '').trim().toLowerCase();
    if (status === 'cancelled') continue;
    const docType = String(inv.document_type ?? '').trim();
    // Storno credit notes reverse cancelled invoices; cancelled rows are excluded above,
    // so counting credit_note snapshots would double-subtract billed quantities.
    if (docType === 'credit_note') continue;
    const delta = extractBilledQuantitiesFromSnapshot(inv.ledger_snapshot, inv.document_type);
    for (const [key, qty] of Object.entries(delta)) {
      totals[key] = (totals[key] ?? 0) + qty;
    }
  }
  return totals;
}

function roundQty(value: number): number {
  return Math.round(value * 1000) / 1000;
}

export function clampInvoiceQty(value: number, remaining: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return roundQty(Math.min(value, Math.max(0, remaining)));
}

export function buildPositionBillingRows(
  offerRows: PositionBillingRowInput[],
  invoicedMap: Record<string, number>,
  invoiceQtyDraft: Record<string, number> | undefined,
  options?: { autoFillRemaining?: boolean },
): PositionBillingRowState[] {
  const autoFill = options?.autoFillRemaining ?? false;
  const out: PositionBillingRowState[] = [];
  for (const row of offerRows) {
    if (!isBillablePositionRowKind(row.kind)) continue;
    const rowKey = billingRowKey(row.leadPositionId, row.id);
    if (!rowKey) continue;
    const offerQty = roundQty(Number(row.quantity) || 0);
    const alreadyInvoicedQty = roundQty(Number(invoicedMap[rowKey]) || 0);
    const remainingQty = roundQty(Math.max(0, offerQty - alreadyInvoicedQty));
    const overInvoiced = alreadyInvoicedQty > offerQty + 0.0001;
    const draftQty = invoiceQtyDraft?.[rowKey];
    const invoiceQty =
      draftQty !== undefined
        ? clampInvoiceQty(Number(draftQty), remainingQty)
        : autoFill
          ? remainingQty
          : 0;
    out.push({
      rowKey,
      rowId: row.id,
      kind: row.kind,
      offerQty,
      alreadyInvoicedQty,
      remainingQty,
      invoiceQty,
      overInvoiced,
    });
  }
  return out;
}

export function buildPositionInvoiceQuantitiesMap(
  billingRows: Pick<PositionBillingRowState, 'rowKey' | 'invoiceQty'>[],
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const row of billingRows) {
    if (row.invoiceQty <= 0) continue;
    out[row.rowKey] = row.invoiceQty;
  }
  return out;
}

export function buildBilledDraftRowsFromBilling(
  sourceRows: OfferDraftRow[],
  billingRows: PositionBillingRowState[],
): OfferDraftRow[] {
  const qtyByRowId = new Map(billingRows.map((r) => [r.rowId, r.invoiceQty]));
  const out: OfferDraftRow[] = [];
  for (const row of sourceRows) {
    if (!isBillablePositionRowKind(row.kind)) continue;
    const invoiceQty = qtyByRowId.get(row.id) ?? 0;
    if (invoiceQty <= 0) continue;
    out.push({
      ...row,
      kind: row.kind === 'apiPosition' ? 'freePosition' : row.kind,
      quantity: invoiceQty,
    });
  }
  return out;
}

export type InvoiceBillingMode = 'percent' | 'amount' | 'position' | 'none';

export function resolveInvoiceBillingMode(
  snapshot: unknown,
  documentType: string,
): InvoiceBillingMode {
  const docType = String(documentType ?? '').trim();
  if (docType === 'partial_invoice') {
    if (!isLedgerSnapshotV1(snapshot)) return 'percent';
    const mode = resolvePartialInvoiceMode(snapshot);
    if (mode === 'position') return 'position';
    if (mode === 'amount') return 'amount';
    return 'percent';
  }
  if (docType === 'invoice' || docType === 'final_invoice' || docType === 'credit_note' || docType === 'invoice_correction') {
    const billed = extractBilledQuantitiesFromSnapshot(snapshot, docType);
    return Object.keys(billed).length > 0 ? 'position' : 'none';
  }
  return 'none';
}
